/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
// TODO: fix type nonsense with Receipts and File
import React from 'react';

import {
  ErrorMessage as FormikError,
  FieldArray,
  FormikProps,
  useFormikContext,
} from 'formik';

import Heading from '../../../../components/typography/Heading';
import {
  GhostButton,
  PrimaryButton,
  SecondaryButton,
  StateButton,
} from '../../../../components/atoms/Button/Button';

import FormikField from '../../../../components/atoms/Form/FormikField';
import FormikInput from '../../../../components/atoms/Form/FormikInput';
import FormikDropdown from '../../../../components/atoms/Form/FormikDropdown';

import {
  createNewExpense,
  type TripExpense,
} from '../../../../types/TripExpense';
import ErrorMessage from '../../../../components/molecules/ErrorMessage';
import FileSelect from '../../../../components/atoms/FileSelect/FileSelect';
import { FinalExpenses } from '../../../../types/FinalExpenses';
import FinalExpensesStats from './FinalExpensesStats';
import Tooltip from '../../../../components/atoms/Tooltip/Tooltip';
import { Receipt } from '../../../../types/Receipt';
import { useModalContext } from '../../../../hooks/ModalProvider/ModalProvider';
import ImageModal from '../../../../components/molecules/ImageModal/ImageModal';

export default function FinalExpensesTable({
  readonly = false,
}: {
  readonly?: boolean;
}) {
  const {
    values: { itemizedExpenses },
    setFieldValue,
  } = useFormikContext<FinalExpenses>();

  const { openModal } = useModalContext();

  const openImageModal = (url: string) => openModal(ImageModal(url));

  return (
    <div>
      <FieldArray name="itemizedExpenses">
        {({ form, insert, remove, push }) => (
          <>
            {!readonly ? (
              <SecondaryButton
                size="small"
                className="max-w-fit"
                onClick={() => push(createNewExpense())}
              >
                + Add Expense
              </SecondaryButton>
            ) : null}
            <table
              className={`my-4 w-full ${
                form.values.itemizedExpenses.length === 0 ? 'hidden' : ''
              }`}
            >
              <thead>
                <tr>
                  <th className="pl-0 sr-only">Cost</th>
                  <th className="pl-0 sr-only">Description</th>
                  <th className="pl-0 sr-only">Expense Category</th>
                  <th className="pl-0 sr-only">Upload Receipt</th>
                  <th className="pl-0 sr-only">Remove Expense</th>
                </tr>
              </thead>
              <tbody className="flex flex-col gap-4">
                {form.values.itemizedExpenses?.map(
                  ({ id, receipt }: TripExpense, index: number) => (
                    <tr
                      key={id || index}
                      className="grid p-4 rounded-md bg-slate-100 grid-cols-1 md:grid-cols-[1fr_1fr_1fr_96px] md:gap-4 w-full"
                    >
                      <div className="md:hidden flex gap-4">
                        <Heading level={3} className="mb-2">
                          Expense #{index + 1}
                        </Heading>
                        {readonly ? (
                          <GhostButton
                            onClick={() => {
                              openImageModal(
                                // @ts-ignore
                                receipt.document as unknown as string
                              );
                            }}
                          >
                            <i className="fa-solid fa-file-lines text-xl md:text-2xl text-primary" />
                          </GhostButton>
                        ) : (
                          <FileSelect
                            id={`receipt${index}`}
                            value={receipt}
                            onChange={(file) =>
                              setFieldValue(
                                `itemizedExpenses.${index}.receipt`,
                                file
                              )
                            }
                            icon
                            title="Upload Receipt"
                            buttonClasses="ml-auto"
                            disabled={readonly}
                          />
                        )}
                        {!readonly ? (
                          <GhostButton
                            onClick={() => remove(index)}
                            className="mt-auto"
                          >
                            <i className="fa-solid fa-trash text-2xl text-error" />
                          </GhostButton>
                        ) : null}
                      </div>
                      <td className="my-2 md:my-0 p-0">
                        <FormikField
                          label="Cost"
                          name={`itemizedExpenses.${index}.cost`}
                          component={FormikInput}
                          type="number"
                          className="!py-0"
                          disabled={readonly}
                        />
                      </td>
                      <td className="my-2 md:my-0 p-0">
                        <FormikField
                          label="Description"
                          name={`itemizedExpenses.${index}.description`}
                          component={FormikInput}
                          className="!py-0"
                          required
                          disabled={readonly}
                        />
                      </td>
                      <td className="my-2 md:my-0 p-0">
                        <FormikDropdown
                          options={[
                            { label: 'Hotel', value: 'Hotel' },
                            { label: 'Ground Travel', value: 'Ground Travel' },
                            { label: 'Air Travel', value: 'Air Travel' },
                            { label: 'Other', value: 'Other' },
                          ]}
                          label="Expense Category"
                          placeholder="Select a category"
                          name={`itemizedExpenses.${index}.title`}
                          buttonClasses="w-full"
                          containerClasses="!py-0"
                          optional={false}
                          disabled={readonly}
                        />
                      </td>
                      <td className="hidden md:flex my-2 md:my-0 justify-center gap-2 p-0">
                        <div className="mt-auto">
                          {readonly ? (
                            <GhostButton
                              onClick={() =>
                                openImageModal(
                                  // @ts-ignore
                                  receipt.document as unknown as string
                                )
                              }
                            >
                              <i className="fa-solid fa-file-lines text-xl md:text-2xl text-primary" />
                            </GhostButton>
                          ) : (
                            <FileSelect
                              id={`receipt${index}`}
                              value={receipt}
                              onChange={(file) =>
                                setFieldValue(
                                  `itemizedExpenses.${index}.receipt`,
                                  file
                                )
                              }
                              icon
                              title="Upload Receipt"
                              disabled={readonly}
                            />
                          )}
                          <div>
                            <FormikError
                              component={ErrorMessage}
                              name={`itemizedExpenses.${index}.receipt`}
                            />
                          </div>
                        </div>
                        {!readonly ? (
                          <GhostButton
                            onClick={() => remove(index)}
                            className="mt-auto"
                          >
                            <i className="fa-solid fa-trash text-xl md:text-2xl text-error" />
                          </GhostButton>
                        ) : null}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
            {/* @see https://formik.org/docs/api/fieldarray#fieldarray-validation-gotchas */}
            {typeof (form as FormikProps<{ itemizedExpenses: TripExpense[] }>)
              ?.errors?.itemizedExpenses === 'string' ? (
              <FormikError component={ErrorMessage} name="itemizedExpenses" />
            ) : null}
          </>
        )}
      </FieldArray>
      {/* <FinalExpensesStats itemizedExpenses={itemizedExpenses} /> */}
    </div>
  );
}
